import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Flame, FileText, Download, FileDown, Loader2, Building2,
} from 'lucide-react';

/*
 * v1.1.9 — Prevenzione Incendi (Studio Tecnico)
 * Wizard per compilare i moduli VVF (PIN 1/2/2-6/3/7).
 */

const COLOR_CLASSES = {
  blue: { border: 'border-blue-200', bg: 'from-blue-500 to-cyan-600', badge: 'bg-blue-100 text-blue-800' },
  amber: { border: 'border-amber-200', bg: 'from-amber-500 to-orange-600', badge: 'bg-amber-100 text-amber-800' },
  purple: { border: 'border-purple-200', bg: 'from-purple-500 to-fuchsia-600', badge: 'bg-purple-100 text-purple-800' },
  emerald: { border: 'border-emerald-200', bg: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-100 text-emerald-800' },
  rose: { border: 'border-rose-200', bg: 'from-rose-500 to-pink-600', badge: 'bg-rose-100 text-rose-800' },
};

export default function PrevenzioneIncendiPage() {
  const [templates, setTemplates] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null); // {kind, ...}
  const [compiling, setCompiling] = useState(false);
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      azienda_id: '', ul_id: '',
      sottoscritto_cognome: '', sottoscritto_nome: '', sottoscritto_cf: '',
      sottoscritto_qualifica: 'Legale Rappresentante',
      sottoscritto_indirizzo: '', sottoscritto_comune: '', sottoscritto_cap: '', sottoscritto_provincia: '', sottoscritto_telefono: '',
      attivita_tipo: '', attivita_indirizzo: '', attivita_comune: '', attivita_cap: '', attivita_provincia: '', attivita_telefono: '',
      attivita_numero_classe: '',
      numero_pratica_vvf: '', comando_vvf_provincia: '', data_documento: new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      note: '',
    };
  }

  useEffect(() => {
    (async () => {
      try {
        const [tRes, aRes, pRes] = await Promise.all([
          api.get('/prevenzione-incendi/templates'),
          api.get('/aziende').catch(() => ({ data: [] })),
          api.get('/pdv').catch(() => ({ data: [] })),
        ]);
        setTemplates(tRes.data || []);
        setAziende(aRes.data || []);
        setPdvList(pRes.data || []);
      } catch (e) {
        toast.error('Errore nel caricamento moduli');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-fill alla selezione azienda
  const onAziendaChange = (azId) => {
    const a = aziende.find((x) => (x.id || x._id) === azId);
    if (a) {
      setForm((f) => ({
        ...f,
        azienda_id: azId,
        ul_id: '',
        sottoscritto_cf: f.sottoscritto_cf || a.codice_fiscale || a.fiscal_code || '',
        sottoscritto_indirizzo: f.sottoscritto_indirizzo || a.sede_legale || a.indirizzo || a.address || '',
        sottoscritto_comune: f.sottoscritto_comune || a.citta || a.comune || a.city || '',
        sottoscritto_cap: f.sottoscritto_cap || a.cap || '',
        sottoscritto_provincia: f.sottoscritto_provincia || a.provincia || a.region || '',
        sottoscritto_telefono: f.sottoscritto_telefono || a.telefono || a.phone || '',
        attivita_indirizzo: f.attivita_indirizzo || a.sede_legale || a.indirizzo || a.address || '',
        attivita_comune: f.attivita_comune || a.citta || a.comune || a.city || '',
        attivita_cap: f.attivita_cap || a.cap || '',
        attivita_provincia: f.attivita_provincia || a.provincia || a.region || '',
        attivita_telefono: f.attivita_telefono || a.telefono || a.phone || '',
        comando_vvf_provincia: f.comando_vvf_provincia || a.provincia || a.region || '',
      }));
    } else {
      setForm((f) => ({ ...f, azienda_id: azId, ul_id: '' }));
    }
  };

  const onUlChange = (ulId) => {
    const p = pdvList.find((x) => (x.id || x._id) === ulId);
    if (p) {
      setForm((f) => ({
        ...f,
        ul_id: ulId,
        attivita_tipo: f.attivita_tipo || p.pdv_type || '',
        attivita_indirizzo: p.address || p.indirizzo || f.attivita_indirizzo,
        attivita_comune: p.city || p.comune || f.attivita_comune,
        attivita_cap: p.cap || f.attivita_cap,
        attivita_provincia: p.province || p.provincia || f.attivita_provincia,
        attivita_telefono: p.phone || p.telefono || f.attivita_telefono,
      }));
    } else {
      setForm((f) => ({ ...f, ul_id: ulId }));
    }
  };

  const downloadBlank = async (kind, label) => {
    try {
      const r = await api.get(`/prevenzione-incendi/templates/${kind}/blank`, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${label.replace(/[^a-zA-Z0-9_-]/g, '_')}_VUOTO.dotx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Template originale scaricato');
    } catch (e) {
      toast.error('Download fallito');
    }
  };

  const compile = async () => {
    setCompiling(true);
    try {
      const r = await api.post('/prevenzione-incendi/compile',
        { kind: activeModule.kind, ...form },
        { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${activeModule.codice}_${activeModule.label.replace(/[^a-zA-Z0-9_-]/g, '_')}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Modulo compilato e scaricato');
      setActiveModule(null);
      setForm(emptyForm());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore compilazione');
    } finally {
      setCompiling(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="prevenzione-incendi-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow">
          <Flame className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Prevenzione Incendi</h1>
          <p className="text-sm text-slate-500">Moduli VV.F. (DPR 151/2011) — compila e scarica i modelli ufficiali pre-compilati</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="moduli-list">
          {templates.map((t) => {
            const c = COLOR_CLASSES[t.color] || COLOR_CLASSES.blue;
            return (
              <Card key={t.kind} className={`border ${c.border} hover:shadow-lg transition`} data-testid={`modulo-${t.kind}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${c.bg} flex items-center justify-center shadow`}>
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <Badge className={`text-xs ${c.badge} border-0`}>{t.codice}</Badge>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t.label}</h3>
                    <p className="text-xs text-slate-500 italic mt-1">{t.norma}</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => setActiveModule(t)}
                      className="bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid={`compile-${t.kind}`}
                    >
                      <FileDown className="w-3 h-3 mr-1" /> Compila
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadBlank(t.kind, t.label)}
                      disabled={!t.blank_available}
                      data-testid={`blank-${t.kind}`}
                    >
                      <Download className="w-3 h-3 mr-1" /> Vuoto
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Compile Dialog */}
      <Dialog open={!!activeModule} onOpenChange={(o) => !o && setActiveModule(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeModule?.codice} — {activeModule?.label}
            </DialogTitle>
            <DialogDescription>
              Seleziona azienda/UL per auto-popolare i campi. Modifica i valori se necessario e clicca &quot;Genera DOCX&quot;.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sezione: Azienda */}
            <div className="bg-slate-50 rounded-xl p-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                <Building2 className="w-4 h-4" /> Azienda / Unità Locale
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Azienda</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={form.azienda_id}
                    onChange={(e) => onAziendaChange(e.target.value)}
                    data-testid="form-azienda"
                  >
                    <option value="">— Seleziona azienda —</option>
                    {aziende.map((a) => (
                      <option key={a.id || a._id} value={a.id || a._id}>
                        {a.denominazione || a.ragione_sociale || a.nome || '—'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Unità Locale</Label>
                  <select
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                    value={form.ul_id}
                    onChange={(e) => onUlChange(e.target.value)}
                    data-testid="form-ul"
                  >
                    <option value="">— Nessuna —</option>
                    {pdvList
                      .filter((p) => !form.azienda_id || p.azienda_id === form.azienda_id)
                      .map((p) => (
                        <option key={p.id || p._id} value={p.id || p._id}>
                          {p.name || p.nome || p.denominazione || '—'}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Sottoscritto */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Il sottoscritto</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField label="Cognome *" value={form.sottoscritto_cognome} onChange={(v) => setForm({ ...form, sottoscritto_cognome: v })} />
                <FormField label="Nome *" value={form.sottoscritto_nome} onChange={(v) => setForm({ ...form, sottoscritto_nome: v })} />
                <FormField label="Codice Fiscale" value={form.sottoscritto_cf} onChange={(v) => setForm({ ...form, sottoscritto_cf: v })} />
                <FormField label="Qualifica" value={form.sottoscritto_qualifica} onChange={(v) => setForm({ ...form, sottoscritto_qualifica: v })} placeholder="es. Legale Rappresentante" />
                <FormField label="Indirizzo domicilio" value={form.sottoscritto_indirizzo} onChange={(v) => setForm({ ...form, sottoscritto_indirizzo: v })} />
                <FormField label="Comune" value={form.sottoscritto_comune} onChange={(v) => setForm({ ...form, sottoscritto_comune: v })} />
                <FormField label="CAP" value={form.sottoscritto_cap} onChange={(v) => setForm({ ...form, sottoscritto_cap: v })} />
                <FormField label="Provincia" value={form.sottoscritto_provincia} onChange={(v) => setForm({ ...form, sottoscritto_provincia: v })} />
                <FormField label="Telefono" value={form.sottoscritto_telefono} onChange={(v) => setForm({ ...form, sottoscritto_telefono: v })} />
              </div>
            </div>

            {/* Attività */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Attività oggetto</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <FormField label="Tipo attività" value={form.attivita_tipo} onChange={(v) => setForm({ ...form, attivita_tipo: v })} placeholder="es. Albergo, Centrale termica" />
                </div>
                <FormField label="N./classe/cat. allegato I" value={form.attivita_numero_classe} onChange={(v) => setForm({ ...form, attivita_numero_classe: v })} placeholder="es. 54.1.A" />
                <FormField label="Indirizzo" value={form.attivita_indirizzo} onChange={(v) => setForm({ ...form, attivita_indirizzo: v })} />
                <FormField label="Comune" value={form.attivita_comune} onChange={(v) => setForm({ ...form, attivita_comune: v })} />
                <FormField label="CAP" value={form.attivita_cap} onChange={(v) => setForm({ ...form, attivita_cap: v })} />
                <FormField label="Provincia" value={form.attivita_provincia} onChange={(v) => setForm({ ...form, attivita_provincia: v })} />
                <FormField label="Telefono attività" value={form.attivita_telefono} onChange={(v) => setForm({ ...form, attivita_telefono: v })} />
              </div>
            </div>

            {/* Pratica VVF */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Riferimenti VV.F.</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormField label="N° Pratica VVF" value={form.numero_pratica_vvf} onChange={(v) => setForm({ ...form, numero_pratica_vvf: v })} placeholder="es. 12345/2026" />
                <FormField label="Comando VVF provincia" value={form.comando_vvf_provincia} onChange={(v) => setForm({ ...form, comando_vvf_provincia: v })} placeholder="es. MILANO" />
                <FormField label="Data documento" value={form.data_documento} onChange={(v) => setForm({ ...form, data_documento: v })} />
              </div>
            </div>

            <div>
              <Label className="text-xs">Note aggiuntive</Label>
              <Textarea
                rows={2}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                data-testid="form-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveModule(null)} disabled={compiling}>
              Annulla
            </Button>
            <Button onClick={compile} disabled={compiling || !form.sottoscritto_cognome.trim() || !form.sottoscritto_nome.trim()}
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
              data-testid="form-compile">
              {compiling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generazione...</> : <><FileDown className="w-4 h-4 mr-2" /> Genera DOCX</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="text-sm" />
    </div>
  );
}
